import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@/lib/auth/useAuth';
import { stringToColor, profileInitials, profileDisplayName } from '@/lib/api/profiles';

function Avatar({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  const { status, profile } = useAuth();
  const dim = size === 'sm' ? 'h-8 w-8 text-[0.7rem]' : 'h-14 w-14 text-xl';

  if (status === 'loading') {
    return <div className={`${dim} animate-pulse rounded-full bg-gray-200`} />;
  }

  if (!profile) {
    return (
      <div className={`${dim} flex items-center justify-center rounded-full bg-gray-300 font-bold text-white`}>
        <svg width="60%" height="60%" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
        </svg>
      </div>
    );
  }

  const color = stringToColor(profile.id);
  const initials = profileInitials(profile);

  return (
    <div
      className={`${dim} flex items-center justify-center rounded-full font-bold text-white`}
      style={{ background: color }}
    >
      {initials}
    </div>
  );
}

export function UserMenu() {
  const { status, profile, user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const history = useHistory();

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (status === 'unauthenticated') {
    return (
      <div className="flex shrink-0 items-center gap-2">
        <button
          onClick={() => history.push('/auth')}
          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:px-3.5"
        >
          Sign in
        </button>
        <button
          onClick={() => history.push('/auth')}
          className="hidden rounded-md bg-[#2f3e46] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] sm:block"
        >
          Subscribe
        </button>
        <button
          onClick={() => history.push('/auth')}
          className="block rounded-md bg-[#2f3e46] px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] sm:hidden"
        >
          Join
        </button>
      </div>
    );
  }

  const displayName = profile ? profileDisplayName(profile) : '';

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="block rounded-full ring-2 ring-transparent transition-all hover:ring-[#1a56a0]/40 focus:outline-none focus:ring-[#1a56a0]/60"
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Avatar size="sm" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-72 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/8"
          role="menu"
        >
          {/* Profile card */}
          <div className="flex flex-col items-center gap-2 bg-gray-50/80 px-4 pb-4 pt-5">
            <Avatar size="lg" />
            {displayName && (
              <p className="mt-0.5 text-sm font-semibold text-gray-900">{displayName}</p>
            )}
            {profile?.handle && (
              <p className="text-xs text-gray-500">@{profile.handle}</p>
            )}
            {user?.email && (
              <p className="text-[0.72rem] text-gray-400">{user.email}</p>
            )}
            {profile?.handle && (
              <button
                onClick={() => { history.push(`/@${profile.handle}`); setOpen(false); }}
                className="mt-1 w-full rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                role="menuitem"
              >
                Manage your account
              </button>
            )}
          </div>

          {/* Links */}
          <div className="border-t border-gray-100 py-1">
            {profile?.handle && (
              <button
                onClick={() => { history.push(`/@${profile.handle}`); setOpen(false); }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                role="menuitem"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
                View profile
              </button>
            )}
            <button
              onClick={() => { history.push('/publications'); setOpen(false); }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              role="menuitem"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              My publications
            </button>
          </div>

          <div className="border-t border-gray-100 py-1">
            <button
              onClick={() => { void signOut(); setOpen(false); }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              role="menuitem"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
